// frontend/src/features/ups/BannedDriverEditModal.js
// This modal displays a banned driver’s details and—if the user is a UPS admin—lets them edit and save the ban reason via the UPS service.

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsUpsAdmin } from '../../features/auth/authSlice';
import { updateBan } from '../../services/upsDriverService';

// Modal for viewing and optionally editing ban details
export default function BannedDriverEditModal({ ban, onClose, onUpdate }) {
  const isAdmin = useSelector(selectIsUpsAdmin);
  const [banReason, setBanReason] = useState(ban?.ban_reason || '');
  const [error, setError] = useState(null);

  const safeDate = v => (v ? new Date(v).toLocaleString() : '—');

  // Submit updated ban reason (admin only)
  const handleSubmit = async e => {
    e.preventDefault();
    if (!isAdmin) return;

    try {
      const reason = banReason.trim();
      if (!reason) return;

      await updateBan(ban?.ban_id, reason);

      // Phase 6 Option A:
      // Parent now treats onUpdate as "refresh the list", not "here is an updated DTO".
      onUpdate?.();

      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error updating ban record');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-md p-6 w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Ban Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-100 p-3 rounded">
            {error}
          </div>
        )}

        {/* Display driver and ban metadata */}
        <div className="space-y-2 mb-4">
          <p><strong>Driver:</strong> {ban?.name || '—'}</p>
          <p><strong>Vehicle:</strong> {ban?.vehicle_registration_number || '—'}</p>
          <p><strong>Phone:</strong> {ban?.phone || '—'}</p>
          <p><strong>Email:</strong> {ban?.email || '—'}</p>
          <p><strong>Agency:</strong> {ban?.workforce_source === 'COMPANY' ? 'Internal' : (ban?.agency_name || '—')}</p>
          <p><strong>Submitted:</strong> {safeDate(ban?.submission_date)}</p>
          <p><strong>Banned At:</strong> {safeDate(ban?.banned_at)}</p>
        </div>

        {/* Edit form: only admins can change ban reason */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="ban-reason" className="block text-gray-700 mb-1">
              Ban Reason
            </label>
            <textarea
              id="ban-reason"
              rows={3}
              required
              value={banReason}
              onChange={e => setBanReason(e.target.value)}
              disabled={!isAdmin}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={!isAdmin}
              title={isAdmin ? 'Update ban reason' : 'Admins only'}
              className={
                'px-4 py-2 rounded ' +
                (isAdmin
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed')
              }
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
